import { useQuery, useMutation, useSubscription } from "urql";
import { useState, useCallback, useEffect, useTransition } from "react";
import {
  txNotificationsQuery,
  markNotificationsAsReadMutation,
  unreadNotificationCountQuery,
  txNotificationSubscription,
} from "../support/mutations";
import { NOTIFICATION_STATUS } from "../constants/notifications";

export const useNotificationsV2 = (networkId = 1) => {
  const [notifications, setNotifications] = useState([]);
  const [readStatus, setReadStatus] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageVariables, setPageVariables] = useState([
    { first: 25, after: "" },
  ]);
  const [isPending, startTransition] = useTransition();

  // Query notifications
  const [{ data, fetching, error }, refetchNotifications] = useQuery({
    query: txNotificationsQuery,
    variables: {
      page: pageVariables[pageVariables.length - 1],
      filter: { networkId },
    },
  });

  // Query unread count
  const [{ data: unreadData }, refetchUnreadCount] = useQuery({
    query: unreadNotificationCountQuery,
    variables: {
      filter: { networkId },
    },
  });

  // Subscribe to new notifications
  const [{ data: subscriptionData }] = useSubscription({
    query: txNotificationSubscription,
    variables: { filter: { networkId } },
  });

  // Mark notifications as read mutation
  const [, markAsRead] = useMutation(markNotificationsAsReadMutation);

  // Handle new notifications from subscription
  useEffect(() => {
    if (subscriptionData?.notifications) {
      const newNotification = subscriptionData.notifications;
      startTransition(() => {
        setNotifications((prev) => [newNotification, ...prev]);
        setReadStatus((prev) => ({
          ...prev,
          [newNotification.id]: newNotification.status,
        }));
        if (newNotification.status === NOTIFICATION_STATUS.UNREAD) {
          setUnreadCount((prev) => prev + 1);
        }
      });
    }
  }, [subscriptionData]);

  // Update notifications and read status when data changes
  useEffect(() => {
    if (data?.notifications?.edges) {
      startTransition(() => {
        if (pageVariables.length === 1) {
          // Initial load
          const newNotifications = data.notifications.edges.map(
            (edge) => edge.node
          );
          setNotifications(newNotifications);

          // Update read status
          const newReadStatus = {};
          newNotifications.forEach((notification) => {
            newReadStatus[notification.id] = notification.status;
          });
          setReadStatus(newReadStatus);
        } else {
          // Append new notifications for pagination
          setNotifications((prev) => {
            const newNotifications = data.notifications.edges.map(
              (edge) => edge.node
            );
            const existingIds = new Set(prev.map((n) => n.id));
            const uniqueNewNotifications = newNotifications.filter(
              (n) => !existingIds.has(n.id)
            );

            // Update read status for new notifications
            const newReadStatus = { ...readStatus };
            uniqueNewNotifications.forEach((notification) => {
              newReadStatus[notification.id] = notification.status;
            });
            setReadStatus(newReadStatus);

            return [...prev, ...uniqueNewNotifications];
          });
        }
        setHasMore(data.notifications.pageInfo.hasNextPage);
      });
    }
  }, [data, pageVariables.length]);

  // Update unread count when data changes
  useEffect(() => {
    if (unreadData?.unreadNotificationCount !== undefined) {
      setUnreadCount(unreadData.unreadNotificationCount);
    }
  }, [unreadData]);

  // Update loading state
  useEffect(() => {
    setIsLoading(fetching);
  }, [fetching]);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    const lastCursor =
      data?.notifications?.edges[data?.notifications?.edges.length - 1]?.cursor;
    if (lastCursor) {
      startTransition(() => {
        setPageVariables((prev) => [...prev, { first: 25, after: lastCursor }]);
      });
    }
  }, [hasMore, isLoading, data]);

  // Mark notifications as read up to a specific timestamp
  const handleMarkAsRead = useCallback(
    async (timestamp) => {
      try {
        // Optimistic update - mark all notifications before timestamp as read
        const newReadStatus = { ...readStatus };
        let updatedCount = 0;

        notifications.forEach((notification) => {
          if (notification.createdAt <= timestamp) {
            newReadStatus[notification.id] = NOTIFICATION_STATUS.READ;
            if (readStatus[notification.id] === NOTIFICATION_STATUS.UNREAD) {
              updatedCount++;
            }
          }
        });

        setReadStatus(newReadStatus);
        setUnreadCount((prev) => Math.max(0, prev - updatedCount));

        // Call mutation
        await markAsRead({
          beforeAtTimestamp: timestamp,
          filter: { networkId },
        });

        // Refetch unread count to ensure accuracy
        await refetchUnreadCount({ requestPolicy: "network-only" });
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
        // Revert optimistic update
        await refetchNotifications({ requestPolicy: "network-only" });
        await refetchUnreadCount({ requestPolicy: "network-only" });
      }
    },
    [
      markAsRead,
      refetchUnreadCount,
      refetchNotifications,
      notifications,
      readStatus,
      networkId,
    ]
  );

  return {
    notifications,
    readStatus,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead: handleMarkAsRead,
    refetchNotifications,
    refetchUnreadCount,
    setNotifications,
  };
};
